// Copyright (c) 2017 VMware, Inc. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//    http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import {
  Component,
  Output,
  EventEmitter,
  OnDestroy,
  Input,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from "@angular/core";

import { Label } from "../service/interface";

import { toPromise, clone, compareValue } from "../utils";

import { LabelService } from "../service/label.service";
import { ErrorHandler } from "../error-handler/error-handler";
import { NgForm } from "@angular/forms";
import { Subject } from "rxjs/Subject";
import { LabelColor } from "../shared/shared.const";

@Component({
  selector: "hbr-create-edit-label",
  template: `<div>
    <form #labelForm="ngForm" [hidden]="!formShow">
        <section>
            <label>
                <label for="name">{{'LABEL.LABEL_NAME' | translate}}</label>
                <label aria-haspopup="true" role="tooltip" [class.invalid]="isLabelNameExist"
                       class="tooltip tooltip-validation tooltip-md tooltip-bottom-left">
                    <input type="text" id="name" name="name" required size="20" autocomplete="off"
                           [(ngModel)]="labelModel.name" #name="ngModel" (keyup)="existValid(labelModel.name)">
                    <span class="tooltip-content">
                      {{'LABEL.NAME_ALREADY_EXISTS' | translate }}
                    </span>
                </label>
            </label>
            <label>
                <label for="color">{{'LABEL.COLOR' | translate}}</label>
                <clr-dropdown [clrCloseMenuOnItemClick]="false">
                    <button type="button" class="btn btn-outline btnColor btn-sm" clrDropdownTrigger>
                        <clr-icon shape="angle down"></clr-icon>
                    </button>
                    <clr-dropdown-menu *clrIfOpen>
                        <label class="dropdown-item" (click)="labelModel.color=i.color" *ngFor="let i of labelColor"
                            [class.borderSty]="i.color == '#FFFFFF'"   [ngStyle]="{'background-color': i.color, 'color': i.textColor }">Aa</label>
                    </clr-dropdown-menu>
                </clr-dropdown>
                <input type="text" id="color" size="8" name="color" disabled [(ngModel)]="labelModel.color" #color="ngModel">
            </label>
            <label>
                <label for="description">{{'LABEL.DESCRIPTION' | translate}}</label>
                <input type="text" id="description" name="description" size="30" [(ngModel)]="labelModel.description"
                       #description="ngModel">
            </label>
            <label>
                <button type="button" class="btn btn-sm btn-outline" (click)="onCancel()" [disabled]="inProgress">{{
                    'BUTTON.CANCEL' | translate }}
                </button>
                <button type="submit" class="btn btn-sm btn-primary" (click)="onSubmit()" [disabled]="!isValid">{{
                    'BUTTON.OK' | translate }}
                </button>
            </label>
        </section>
    </form>
</div>`,
  styles: [`form{margin-bottom:-10px;padding-top:0;margin-top:20px;width:100%;background-color:#eee;border:1px solid #ccc}form section{padding:.5rem 0}form section>label{margin-left:20px}form .btnColor{margin:0!important;padding:0!important;width:26px;height:22px;min-width:26px}form .colorPanel span{margin:5px 4px;width:30px;height:24px;text-align:center;line-height:24px;font-size:12px;border:1px solid #a1a1a1}form .dropdown-menu{display:inline-block;width:166px;padding:6px}form .dropdown-menu .dropdown-item{display:inline-block;margin:2px 4px;padding:0;width:30px;height:24px;text-align:center;line-height:24px;border:0;color:#fff;font-size:12px}form .dropdown .dropdown-toggle.btn{padding:0!important}form .borderSty{border:1px solid #a1a1a1!important;line-height:22px}`],
  changeDetection: ChangeDetectionStrategy.Default
})
export class CreateEditLabelComponent implements OnInit, OnDestroy {
  formShow: boolean;
  inProgress: boolean;
  copeLabelModel: Label;
  labelModel: Label = this.initLabel();
  labelId = 0;

  isLabelNameExist = false;

  nameChecker = new Subject<string>();

  labelForm: NgForm;
  @ViewChild("labelForm") currentForm: NgForm;

  @Input() projectId: number;
  @Input() scope: string;
  @Output() reload = new EventEmitter();

  constructor(
    private labelService: LabelService,
    private errorHandler: ErrorHandler,
    private ref: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.nameChecker.debounceTime(500).subscribe((name: string) => {
      toPromise<Label[]>(
        this.labelService.getLabels(this.scope, this.projectId, name)
      )
        .then(targets => {
          this.isLabelNameExist = false;
          if (targets && targets.length) {
            if (targets.find((target) => {
              return target.name === name;
            })) {
              this.isLabelNameExist = true;
            }
          }
        })
        .catch(error => {
          this.errorHandler.error(error);
        });
      let hnd = setInterval(() => this.ref.markForCheck(), 100);
      setTimeout(() => clearInterval(hnd), 5000);
    });
  }

  ngOnDestroy(): void {
    this.nameChecker.unsubscribe();
  }

  get labelColor() {
    return LabelColor;
  }

  initLabel(): Label {
    return {
      name: "",
      description: "",
      color: "",
      scope: "",
      project_id: 0
    };
  }
  openModal(): void {
    this.labelModel = this.initLabel();
    this.formShow = true;
    this.isLabelNameExist = false;
    this.labelId = 0;
    this.copeLabelModel = null;
  }

  editModel(labelId: number, label: Label[]): void {
    this.labelModel = clone(label[0]);
    this.formShow = true;
    this.labelId = labelId;
    this.copeLabelModel = clone(label[0]);
  }

  public get hasChanged(): boolean {
    return !compareValue(this.copeLabelModel, this.labelModel);
  }

  public get isValid(): boolean {
    return !(
      this.isLabelNameExist ||
      !(this.currentForm && this.currentForm.valid) ||
      !this.hasChanged ||
      this.inProgress
    );
  }

  existValid(text: string): void {
    if (text) {
      this.nameChecker.next(text);
    } else {
      this.isLabelNameExist = false;
    }
  }

  onSubmit(): void {
    this.inProgress = true;
    if (this.labelId <= 0) {
      this.labelModel.scope = this.scope;
      this.labelModel.project_id = this.projectId;
      toPromise<Label>(this.labelService.createLabel(this.labelModel))
        .then(res => {
          this.inProgress = false;
          this.reload.emit();
          this.labelModel = this.initLabel();
          this.formShow = false;
        })
        .catch(err => {
          this.inProgress = false;
          this.errorHandler.error(err);
        });
    } else {
      toPromise<Label>(
        this.labelService.updateLabel(this.labelId, this.labelModel)
      )
        .then(res => {
          this.inProgress = false;
          this.reload.emit();
          this.labelModel = this.initLabel();
          this.formShow = false;
        })
        .catch(err => {
          this.inProgress = false;
          this.errorHandler.error(err);
        });
    }
  }

  onCancel(): void {
    this.inProgress = false;
    this.labelModel = this.initLabel();
    this.formShow = false;
  }
}
